import { AppShell, MantineProvider, createStyles } from '@mantine/core';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { IconBarrierBlock, IconWifiOff } from '@tabler/icons-react';

import Logo from '~/domains/common/components/AppShellHeader/components/Header/Logo';
import { HEADER_HEIGHT } from '~/domains/common/components/AppShellHeader/constants';
import { FullScreenMessage } from '~/domains/common/components/FullScreenMessage/FullScreenMessage';

type ErrorBounadryScreenProps = {
  clearError?: () => void;
  error?: Error;
};

const useStyles = createStyles((theme) => ({
  container: {
    background: theme.black,
    zIndex: theme.other.zIndex.header,
    height: HEADER_HEIGHT,
    position: 'sticky',
    top: 0,
  },
  content: {
    maxWidth: theme.other.layout.maxWidth,
    paddingLeft: theme.other.layout.paddingLg,
    paddingRight: theme.other.layout.paddingLg,
    margin: '0 auto',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._40,
    height: '100%',

    [theme.fn.smallerThan('lg')]: {
      gap: /* eslint-disable-line local-rules/use-theme-tokens */ 20,
      paddingLeft: theme.other.layout.paddingMd,
      paddingRight: theme.other.layout.paddingMd,
    },
    [theme.fn.smallerThan('md')]: {
      paddingLeft: theme.other.layout.paddingSm,
      paddingRight: theme.other.layout.paddingSm,
    },
  },
}));

function ErrorBounadryScreenHeader() {
  const { classes } = useStyles();
  return (
    <div className={classes.container}>
      <div className={classes.content}>
        <Logo />
      </div>
    </div>
  );
}

function ErrorBounadryScreen({ clearError, error }: ErrorBounadryScreenProps) {
  const router = useRouter();
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (!clearError) {
      return;
    }

    router.events.on('routeChangeComplete', clearError);

    return () => {
      router.events.off('routeChangeComplete', clearError);
    };
  }, [clearError, router.events]);

  const renderErrorMessage = () => {
    if (isOffline) {
      return (
        <FullScreenMessage
          icon={<IconWifiOff />}
          title="You're offline"
          subtitle="Please check your internet connection and try again."
        />
      );
    }

    if (error instanceof Error && (error.name === 'TypeError' || error.name === 'SyntaxError')) {
      return (
        <FullScreenMessage
          icon={<IconBarrierBlock />}
          title="The site is broken"
          subtitle="We're experiencing technical difficulties. Please try again later."
        />
      );
    }

    return (
      <FullScreenMessage
        icon={<IconBarrierBlock />}
        title="An error occurred"
        subtitle={
          error instanceof Error && error.message
            ? error.message
            : 'Something went wrong with this page.'
        }
      />
    );
  };

  return (
    <MantineProvider inherit theme={{ colorScheme: 'light' }}>
      <AppShell
        styles={{
          main: {
            display: 'flex',
            flexFlow: 'column nowrap',
            padding: 0,
            minHeight: 'calc(100vh - 122px)',
            width: '100%',
          },
        }}
        padding={0}
        header={<ErrorBounadryScreenHeader />}
      >
        {renderErrorMessage()}
      </AppShell>
    </MantineProvider>
  );
}

export default ErrorBounadryScreen;
